import type { NotificationDto, PageMeta, Role } from '@talentbabu/types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, http } from '../../lib/api';

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: async () => (await apiGet<{ count: number }>('/notifications/unread-count')).data.count,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useNotifications(page: number, limit = 20) {
  return useQuery({
    queryKey: ['notifications', 'list', page, limit],
    queryFn: async () => {
      const res = await apiGet<NotificationDto[]>('/notifications', { params: { page, limit } });
      return { items: res.data, meta: res.meta as PageMeta & { unread: number } };
    },
    placeholderData: keepPreviousData,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | 'all') =>
      id === 'all' ? apiPost('/notifications/read-all') : http.patch(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** The area whose support screens a role uses. */
function supportBase(role: Role): string | null {
  if (role === 'CANDIDATE') return '/candidate';
  if (role === 'EMPLOYER' || role === 'HR_MANAGER') return '/employer';
  return ['SUPER_ADMIN', 'ADMIN', 'FINANCE', 'SUPPORT'].includes(role) ? '/admin' : null;
}

/** Where a Support Desk / Help & Support notification opens: the exact ticket, inside the viewer's own area. */
export function supportTicketUrl(
  n: Pick<NotificationDto, 'entityType' | 'entityId'>,
  role: Role | undefined,
): string | undefined {
  if (n.entityType !== 'SupportTicket' || !n.entityId || !role) return undefined;
  const base = supportBase(role);
  return base ? `${base}/support/${n.entityId}` : undefined;
}

/** Links are stored employer-style; staff open the same screens under their own area. */
export function localizeNotificationUrl(url: string | undefined, role: Role | undefined): string | undefined {
  if (!url || !role) return url;
  // Support notifications without a ticket id fall back to the viewer's own support list, never a bare /support.
  if (url === '/support' || url === '/admin/support') {
    const base = supportBase(role);
    return base ? `${base}/support` : url;
  }
  const base =
    role === 'RECRUITER'
      ? '/recruiter'
      : ['SUPER_ADMIN', 'ADMIN', 'FINANCE', 'SUPPORT'].includes(role)
        ? '/admin'
        : null;
  if (!base || !url.startsWith('/employer/')) return url;
  // Staff see employers' “Hires” screen as “Placements”.
  return url.replace('/employer/hires', '/employer/placements').replace('/employer', base);
}
